import { ExternalLinkIcon } from '@radix-ui/react-icons'
import { Button } from '@radix-ui/themes'
import { useWallet } from '@solana/wallet-adapter-react'
import { AuthButton } from 'features/auth/components/auth-buttons'
import { getWalletAppOptions } from 'features/auth/utils/get-solana-connector-providers'
import type { SolanaConnectorProvider } from 'features/auth/utils/get-solana-connector-providers'
import { DataTestIDs } from 'shared/constants'
import { WalletIcon } from 'shared/icons'
import { RadixText, XyroLoading } from 'shared/ui'
import { ProviderIcon } from './provider-icon'
import styles from '../neon-wallets.module.scss'

interface Props {
  isBusy: boolean
  onPick: (provider: SolanaConnectorProvider) => void
  /** Button text for a wallet; its name by default. */
  getLabel?: (provider: SolanaConnectorProvider) => string
  buttonTestId: DataTestIDs
  /** Full-width buttons (Connect wallet) or a grid of neon tiles (Your wallets). */
  variant?: 'buttons' | 'tiles'
}

type Option = ReturnType<typeof getWalletAppOptions>[number]

interface ItemProps extends Omit<Props, 'variant' | 'getLabel'> {
  option: Option
  label: string
}

const InstallTile: React.FC<{ option: Option }> = ({ option }) => (
  <a
    className={styles.appTile}
    data-installed={false}
    href={option.provider.installUrl}
    target='_blank'
    rel='noreferrer'
    data-testid={DataTestIDs.linkWalletInstall}
  >
    <span className={styles.appTileIcon}>
      <ProviderIcon
        provider={option.provider}
        fallback={<WalletIcon />}
      />
    </span>
    <span className={styles.appTileName}>{option.provider.name}</span>
    <span className={styles.appTileHint}>
      Install <ExternalLinkIcon />
    </span>
  </a>
)

const AppTile: React.FC<ItemProps> = ({
  option,
  label,
  isBusy,
  onPick,
  buttonTestId
}) =>
  option.isInstalled ?
    <button
      type='button'
      className={styles.appTile}
      data-installed
      disabled={isBusy}
      onClick={() => onPick(option.provider)}
      data-testid={buttonTestId}
    >
      <span className={styles.appTileIcon}>
        <ProviderIcon
          provider={option.provider}
          adapterIcon={option.icon}
          fallback={<WalletIcon />}
        />
      </span>
      <span className={styles.appTileName}>{label}</span>
    </button>
  : <InstallTile option={option} />

const AppButton: React.FC<ItemProps> = ({
  option,
  label,
  isBusy,
  onPick,
  buttonTestId
}) => {
  const { provider, isInstalled, icon } = option

  if (!isInstalled) {
    return (
      <Button
        asChild
        variant='soft'
        color='gray'
        size='3'
        data-testid={DataTestIDs.linkWalletInstall}
      >
        <a
          href={provider.installUrl}
          target='_blank'
          rel='noreferrer'
        >
          Install {provider.name}
        </a>
      </Button>
    )
  }

  return (
    <AuthButton
      disabled={isBusy}
      onClick={() => onPick(provider)}
      dataTestID={buttonTestId}
    >
      <XyroLoading loading={isBusy}>
        <ProviderIcon
          provider={provider}
          adapterIcon={icon}
        />
        <RadixText>{label}</RadixText>
      </XyroLoading>
    </AuthButton>
  )
}

/**
 * The wallet apps a user can pick (FE-306): named wallets, installed or with
 * an "Install …" link, plus every other installed Solana wallet. Shared by
 * linking (Your wallets) and connecting (Connect wallet).
 */
export const WalletAppList: React.FC<Props> = ({
  getLabel = provider => provider.name,
  variant = 'buttons',
  ...itemProps
}) => {
  const { wallets } = useWallet()
  const options = getWalletAppOptions(wallets).filter(
    option => option.isInstalled || option.provider.installUrl
  )
  const Item = variant === 'tiles' ? AppTile : AppButton

  const items = options.map(option => (
    <Item
      key={option.provider.id}
      option={option}
      label={getLabel(option.provider)}
      {...itemProps}
    />
  ))

  return variant === 'tiles' ?
      <div className={styles.appGrid}>{items}</div>
    : <>{items}</>
}
